import {
  ChatMessageWithParts,
  ContentType,
  TMessageContentPart,
} from "src/views/assistant/types";
import { memo } from "react";

import ToolCall from "./ToolCall";
import { extractToolCallId } from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";
import ToolResponse from "./ToolResponse";

type ArtifactProps = {
  chatMessage: ChatMessageWithParts;
  part: TMessageContentPart;
  partIndex: number;
  isChatConnected: boolean;
  isChatMessageStreamStopped?: boolean;
};

const Artifact = memo(
  ({
    part,
    partIndex,
    isChatConnected,
    isChatMessageStreamStopped,
    chatMessage,
  }: ArtifactProps) => {
    switch (part.type) {
      case ContentType.TOOL_CALL: {
        // Pair this call with its own response. Match by tool_call_id —
        // with parallel tool calls, a sibling's response can arrive while
        // this call is still running, so positional pairing would flip the
        // card to completed (or attach the wrong result) prematurely.
        // Ordinal pairing covers legacy parts without ids.
        const parts = chatMessage?.contentParts ?? [];
        const callId = extractToolCallId(part.text);
        let responsePart: TMessageContentPart | undefined;
        if (callId) {
          responsePart = parts.find(
            (p) =>
              p.type === ContentType.TOOL_RESPONSE &&
              extractToolCallId(p.text) === callId,
          );
        } else {
          let callOrdinal = 0;
          for (let i = 0; i < partIndex; i++) {
            if (parts[i]?.type === ContentType.TOOL_CALL) callOrdinal++;
          }
          let responsesSeen = 0;
          for (const p of parts) {
            if (p.type !== ContentType.TOOL_RESPONSE) continue;
            if (responsesSeen === callOrdinal) {
              responsePart = p;
              break;
            }
            responsesSeen++;
          }
        }
        return (
          <ToolCall
            text={part.text}
            partIndex={partIndex}
            messageId={chatMessage?.id}
            parentMessageId={chatMessage?.parent_id}
            hasResponse={Boolean(responsePart)}
            responseText={responsePart?.text}
            isChatConnected={isChatConnected}
            isChatMessageStreamStopped={isChatMessageStreamStopped}
          />
        );
      }

      case ContentType.TOOL_RESPONSE:
        return (
          <ToolResponse
            text={part.text}
            partIndex={partIndex}
            messageId={chatMessage?.id}
            parentMessageId={chatMessage?.parent_id}
            isChatConnected={isChatConnected}
          />
        );
    }
  },
);

export default Artifact;
